/**
 * TaxReconciliation - Basis-Aware P&L vs 1065/K-1 Cross-Check Component
 * 
 * Compares bank-derived or client-provided P&L totals against tax-reported 1065 figures
 * with thresholds that adapt based on accounting basis (cash vs accrual).
 * 
 * Based on Darryl's guidance:
 * - Tax returns are almost always cash basis
 * - Bank-derived P&Ls are inherently cash basis
 * - Client P&Ls from QuickBooks may be either basis - must be declared and disclosed
 */

import React, { useState, useMemo } from 'react';
import { 
  CheckCircle2, 
  AlertTriangle, 
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Scale,
  FileText,
  Calculator,
  Settings2
} from 'lucide-react';

interface TaxReconciliationProps {
  plData: Record<string, any[]>;  // P&L data by year (from canonical_data)
  tax1065Files: any[];            // 1065 form data
  k1Forms: any[];                 // K-1 form data
}

interface YearComparison {
  year: number;
  revenue: { pl: number; tax: number; diff: number; pct: number };
  payroll: { pl: number; tax: number; diff: number; pct: number };
  cogs: { pl: number; tax: number; diff: number; pct: number };
  status: 'match' | 'review' | 'investigate';
}

// Basis options with their thresholds
const basisOptions = [
  { 
    id: 'bank_cash', 
    label: 'Bank-Derived (Cash) vs 1065 (Cash)', 
    shortLabel: 'Bank vs 1065',
    description: 'Tightest thresholds — both cash basis from same underlying transactions',
    thresholds: { review: 5, investigate: 10 },
    color: 'emerald'
  },
  { 
    id: 'client_cash', 
    label: 'Client P&L (Cash) vs 1065 (Cash)', 
    shortLabel: 'Client Cash vs 1065',
    description: 'Tight thresholds — allow for QuickBooks categorization differences',
    thresholds: { review: 8, investigate: 15 },
    color: 'blue'
  },
  { 
    id: 'client_accrual', 
    label: 'Client P&L (Accrual) vs 1065 (Cash)', 
    shortLabel: 'Accrual vs Cash',
    description: 'Wider thresholds — AR/AP timing differences are expected',
    thresholds: { review: 15, investigate: 25 },
    color: 'amber'
  },
];

// Basis-specific notes for each line item
const basisNotes: Record<string, Record<string, string>> = {
  revenue: {
    bank_cash: "Both cash basis — variance should be small. Large gap suggests non-revenue deposits (transfers, loans, owner contributions).",
    client_cash: "Both cash basis — check for QuickBooks categorization differences or unrecorded adjustments.",
    client_accrual: "Expected: accrual revenue includes unbilled AR. Compare year-end AR balances to explain gap."
  },
  payroll: {
    bank_cash: "Check guaranteed payments to partners, 1099 vs W-2 classification, officer compensation.",
    client_cash: "Review for contractor payments categorized as payroll, or benefits included in one but not the other.",
    client_accrual: "Accrued payroll liabilities may cause timing differences. Check year-end accruals."
  },
  cogs: {
    bank_cash: "Should align closely. Check for inventory purchases vs COGS timing on tax return.",
    client_cash: "Minor variance from categorization. Verify Section 263A if applicable.",
    client_accrual: "AP timing will cause variance. Check prepaid expenses and inventory accruals."
  }
};

const formatCurrency = (n: number): string => {
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const formatPercent = (pct: number): string => {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

const getVarianceStatus = (pct: number, basisId: string): { 
  status: 'match' | 'review' | 'investigate'; 
  label: string; 
  colorClass: string;
  bgClass: string;
  borderClass: string;
} => {
  const basis = basisOptions.find(b => b.id === basisId) || basisOptions[0];
  const abs = Math.abs(pct);
  
  if (abs <= basis.thresholds.review) {
    return { 
      status: 'match', 
      label: 'Within Tolerance', 
      colorClass: 'text-green-700',
      bgClass: 'bg-green-50',
      borderClass: 'border-green-200'
    };
  }
  if (abs <= basis.thresholds.investigate) {
    return { 
      status: 'review', 
      label: 'Review Needed', 
      colorClass: 'text-amber-700',
      bgClass: 'bg-amber-50',
      borderClass: 'border-amber-200'
    };
  }
  return { 
    status: 'investigate', 
    label: 'Investigate', 
    colorClass: 'text-red-700',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200'
  };
};

const TaxReconciliation: React.FC<TaxReconciliationProps> = ({ 
  plData, 
  tax1065Files, 
  k1Forms 
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [yearBasis, setYearBasis] = useState<Record<number, string>>({});
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Calculate annual P&L totals from canonical data
  const plAnnualTotals = useMemo(() => {
    const totals: Record<number, { revenue: number; payroll: number; cogs: number }> = {};
    
    if (!plData) return totals;
    
    Object.entries(plData).forEach(([yearKey, months]) => {
      const yearMatch = yearKey.match(/(\d{4})/);
      if (!yearMatch) return;
      const year = parseInt(yearMatch[1]);
      
      if (!totals[year]) {
        totals[year] = { revenue: 0, payroll: 0, cogs: 0 };
      }
      
      (months as any[]).forEach(m => {
        totals[year].revenue += m.revenue || m.gross_revenue || 0;
        totals[year].payroll += m.payroll || m.employee_wages || 0;
        totals[year].cogs += m.cogs || 0;
      });
    });
    
    return totals;
  }, [plData]);

  // Extract 1065 data by year
  const tax1065ByYear = useMemo(() => {
    const byYear: Record<number, any> = {};
    
    tax1065Files.forEach(file => {
      const taxData = file.data || {};
      const year = taxData.tax_year || file.year;
      if (year) {
        byYear[year] = {
          grossReceipts: taxData.gross_receipts || 0,
          cogs: taxData.cost_of_goods_sold || taxData.cogs || 0,
          wages: taxData.salaries_wages || 0,
          guaranteedPayments: taxData.guaranteed_payments || 0
        };
      }
    });
    
    return byYear;
  }, [tax1065Files]);

  // Initialize year basis defaults
  useMemo(() => {
    const years = new Set([
      ...Object.keys(plAnnualTotals).map(Number),
      ...Object.keys(tax1065ByYear).map(Number)
    ]);
    
    const defaults: Record<number, string> = {};
    years.forEach(year => {
      if (!yearBasis[year]) {
        defaults[year] = 'bank_cash'; // Default to bank-derived
      }
    });
    
    if (Object.keys(defaults).length > 0) {
      setYearBasis(prev => ({ ...prev, ...defaults }));
    }
  }, [plAnnualTotals, tax1065ByYear]);

  // Build year comparisons
  const yearComparisons = useMemo((): YearComparison[] => {
    const years = new Set([
      ...Object.keys(plAnnualTotals).map(Number),
      ...Object.keys(tax1065ByYear).map(Number)
    ]);
    
    return Array.from(years)
      .filter(year => plAnnualTotals[year] && tax1065ByYear[year])
      .sort((a, b) => a - b)
      .map(year => {
        const pl = plAnnualTotals[year];
        const tax = tax1065ByYear[year];
        const basis = yearBasis[year] || 'bank_cash';
        
        const revDiff = pl.revenue - tax.grossReceipts;
        const revPct = tax.grossReceipts ? (revDiff / tax.grossReceipts) * 100 : 0;
        
        const payrollDiff = pl.payroll - tax.wages;
        const payrollPct = tax.wages ? (payrollDiff / tax.wages) * 100 : 0;
        
        const cogsDiff = pl.cogs - tax.cogs;
        const cogsPct = tax.cogs ? (cogsDiff / tax.cogs) * 100 : 0;
        
        const maxVariance = Math.max(Math.abs(revPct), Math.abs(payrollPct), Math.abs(cogsPct));
        const basisThresholds = basisOptions.find(b => b.id === basis)?.thresholds || { review: 5, investigate: 10 };
        
        let status: 'match' | 'review' | 'investigate' = 'match';
        if (maxVariance > basisThresholds.investigate) status = 'investigate';
        else if (maxVariance > basisThresholds.review) status = 'review';
        
        return {
          year,
          revenue: { pl: pl.revenue, tax: tax.grossReceipts, diff: revDiff, pct: revPct },
          payroll: { pl: pl.payroll, tax: tax.wages, diff: payrollDiff, pct: payrollPct },
          cogs: { pl: pl.cogs, tax: tax.cogs, diff: cogsDiff, pct: cogsPct },
          status
        };
      });
  }, [plAnnualTotals, tax1065ByYear, yearBasis]);

  const toggleRow = (rowId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const handleBasisChange = (year: number, basisId: string) => {
    setYearBasis(prev => ({ ...prev, [year]: basisId }));
  };

  // Summary stats
  const summaryStats = useMemo(() => {
    const total = yearComparisons.length;
    const matches = yearComparisons.filter(y => y.status === 'match').length;
    const reviews = yearComparisons.filter(y => y.status === 'review').length;
    const investigations = yearComparisons.filter(y => y.status === 'investigate').length;
    return { total, matches, reviews, investigations };
  }, [yearComparisons]);

  if (tax1065Files.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
        <Scale className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600 font-medium">No tax documents to reconcile</p>
        <p className="text-sm text-gray-500 mt-2">Upload 1065 forms to enable P&L cross-check</p>
      </div>
    );
  }

  if (yearComparisons.length === 0) {
    return (
      <div className="text-center py-12 bg-amber-50 rounded-xl border border-amber-200">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
        <p className="text-amber-800 font-medium">No matching years found</p>
        <p className="text-sm text-amber-600 mt-2">
          P&L data and 1065 forms don't have overlapping years for comparison
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with explanation */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-200 p-5">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Scale className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-indigo-900 mb-1">Basis-Aware P&L ↔ 1065 Reconciliation</h3>
            <p className="text-sm text-indigo-700 leading-relaxed">
              Thresholds adapt based on accounting basis. Tax returns are almost always cash basis. 
              Bank-derived P&Ls are inherently cash. Client P&Ls may be either — <span className="font-medium">must be declared per engagement</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Basis Threshold Reference */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-gray-500" />
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Basis Pair → Threshold Matrix</span>
        </div>
        <div className="divide-y divide-gray-100">
          {basisOptions.map(opt => (
            <div key={opt.id} className="grid grid-cols-12 gap-4 px-5 py-3 items-center">
              <div className="col-span-6">
                <div className={`text-sm font-medium ${
                  opt.color === 'emerald' ? 'text-emerald-700' :
                  opt.color === 'blue' ? 'text-blue-700' : 'text-amber-700'
                }`}>
                  {opt.shortLabel}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
              </div>
              <div className="col-span-3 text-center">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                  Review: ≤{opt.thresholds.review}%
                </span>
              </div>
              <div className="col-span-3 text-center">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                  Investigate: &gt;{opt.thresholds.investigate}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{summaryStats.total}</div>
          <div className="text-xs text-gray-500 mt-1">Years Compared</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4 text-center">
          <div className="text-2xl font-bold text-green-700">{summaryStats.matches}</div>
          <div className="text-xs text-green-600 mt-1">Within Tolerance</div>
        </div>
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 text-center">
          <div className="text-2xl font-bold text-amber-700">{summaryStats.reviews}</div>
          <div className="text-xs text-amber-600 mt-1">Need Review</div>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4 text-center">
          <div className="text-2xl font-bold text-red-700">{summaryStats.investigations}</div>
          <div className="text-xs text-red-600 mt-1">Investigate</div>
        </div>
      </div>

      {/* Year-by-Year Comparison */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-gray-600" />
            Annual Variance Analysis
          </h3>
        </div>
        
        <div className="divide-y divide-gray-100">
          {yearComparisons.map(comp => {
            const yearKey = `year-${comp.year}`;
            const isExpanded = expandedRows.has(yearKey);
            const currentBasis = yearBasis[comp.year] || 'bank_cash';
            const basisInfo = basisOptions.find(b => b.id === currentBasis) || basisOptions[0];
            const overallStatus = getVarianceStatus(
              Math.max(Math.abs(comp.revenue.pct), Math.abs(comp.payroll.pct), Math.abs(comp.cogs.pct)),
              currentBasis
            );
            
            return (
              <div key={comp.year}>
                {/* Year Header Row */}
                <div 
                  className={`px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}
                  onClick={() => toggleRow(yearKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-bold text-gray-900">{comp.year}</span>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${overallStatus.bgClass} ${overallStatus.borderClass} border ${overallStatus.colorClass}`}>
                        {overallStatus.status === 'match' && <CheckCircle2 className="w-3.5 h-3.5" />}
                        {overallStatus.status === 'review' && <AlertTriangle className="w-3.5 h-3.5" />}
                        {overallStatus.status === 'investigate' && <AlertCircle className="w-3.5 h-3.5" />}
                        {overallStatus.label}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                        basisInfo.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                        basisInfo.color === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {currentBasis === 'bank_cash' ? 'BANK' : currentBasis === 'client_cash' ? 'CASH' : 'ACCRUAL'}
                      </span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Revenue</div>
                        <div className={`font-mono font-semibold ${comp.revenue.pct > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatPercent(comp.revenue.pct)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Payroll</div>
                        <div className={`font-mono font-semibold ${comp.payroll.pct > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatPercent(comp.payroll.pct)}
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-4">
                    {/* Basis Selector */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Accounting Basis — {comp.year}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            Select the basis pair to apply correct variance thresholds
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          Required Disclosure
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {basisOptions.map(opt => {
                          const isActive = currentBasis === opt.id;
                          return (
                            <button
                              key={opt.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBasisChange(comp.year, opt.id);
                              }}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                isActive 
                                  ? `${opt.color === 'emerald' ? 'border-emerald-300 bg-emerald-50' : 
                                       opt.color === 'blue' ? 'border-blue-300 bg-blue-50' : 'border-amber-300 bg-amber-50'}`
                                  : 'border-gray-200 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <div className={`text-sm font-medium ${
                                isActive 
                                  ? (opt.color === 'emerald' ? 'text-emerald-700' : 
                                     opt.color === 'blue' ? 'text-blue-700' : 'text-amber-700')
                                  : 'text-gray-600'
                              }`}>
                                {opt.shortLabel}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Review: {opt.thresholds.review}% · Investigate: {opt.thresholds.investigate}%
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Line Item Cards */}
                    <div className="grid grid-cols-3 gap-3">
                      <VarianceCard
                        label="Revenue / Gross Receipts"
                        plValue={comp.revenue.pl}
                        taxValue={comp.revenue.tax}
                        diff={comp.revenue.diff}
                        pct={comp.revenue.pct}
                        basisId={currentBasis}
                        note={basisNotes.revenue[currentBasis]}
                        basisLabel={currentBasis === 'client_accrual' ? 'Accrual' : 'Cash'}
                      />
                      <VarianceCard
                        label="Payroll / Wages"
                        plValue={comp.payroll.pl}
                        taxValue={comp.payroll.tax}
                        diff={comp.payroll.diff}
                        pct={comp.payroll.pct}
                        basisId={currentBasis}
                        note={basisNotes.payroll[currentBasis]}
                        basisLabel={currentBasis === 'client_accrual' ? 'Accrual' : 'Cash'}
                      />
                      <VarianceCard
                        label="COGS / Direct Costs"
                        plValue={comp.cogs.pl}
                        taxValue={comp.cogs.tax}
                        diff={comp.cogs.diff}
                        pct={comp.cogs.pct}
                        basisId={currentBasis}
                        note={basisNotes.cogs[currentBasis]}
                        basisLabel={currentBasis === 'client_accrual' ? 'Accrual' : 'Cash'}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Decision Rules Reference */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Core Principle
        </h4>
        <p className="text-sm text-slate-600 leading-relaxed">
          Tax returns are almost always cash basis. Bank-derived P&Ls are inherently cash basis. 
          When both are cash, variances should be small and explainable. Client-provided P&Ls from 
          QuickBooks may be either basis — <span className="font-medium">must be declared and disclosed per engagement</span>.
        </p>
      </div>
    </div>
  );
};

// Sub-component for variance cards
const VarianceCard: React.FC<{
  label: string;
  plValue: number;
  taxValue: number;
  diff: number;
  pct: number;
  basisId: string;
  note: string;
  basisLabel: string;
}> = ({ label, plValue, taxValue, diff, pct, basisId, note, basisLabel }) => {
  const status = getVarianceStatus(pct, basisId);
  const basis = basisOptions.find(b => b.id === basisId) || basisOptions[0];
  
  return (
    <div className={`rounded-lg border p-4 ${status.bgClass} ${status.borderClass}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-medium text-gray-800 text-sm">{label}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${status.colorClass}`}>
          {status.status === 'match' && <CheckCircle2 className="w-3 h-3" />}
          {status.status === 'review' && <AlertTriangle className="w-3 h-3" />}
          {status.status === 'investigate' && <AlertCircle className="w-3 h-3" />}
          {status.label}
        </span>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">P&L ({basisLabel})</span>
          <span className="font-mono font-semibold text-gray-900">{formatCurrency(plValue)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">1065 (Cash)</span>
          <span className="font-mono font-semibold text-blue-700">{formatCurrency(taxValue)}</span>
        </div>
        <div className="pt-2 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">Variance</span>
            <span className={`font-mono font-bold ${pct > 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {diff > 0 ? '+' : ''}{formatCurrency(diff)} ({formatPercent(pct)})
            </span>
          </div>
        </div>
      </div>
      
      {/* Variance bar with threshold markers */}
      <div className="mt-3 h-1.5 bg-gray-200 rounded-full overflow-hidden relative">
        {/* Threshold markers */}
        <div 
          className="absolute h-3 w-0.5 bg-amber-400 -top-0.5" 
          style={{ left: `${Math.min(basis.thresholds.review * 3, 100)}%` }}
        />
        <div 
          className="absolute h-3 w-0.5 bg-red-400 -top-0.5" 
          style={{ left: `${Math.min(basis.thresholds.investigate * 3, 100)}%` }}
        />
        <div 
          className={`h-full rounded-full transition-all ${
            status.status === 'match' ? 'bg-green-500' : 
            status.status === 'review' ? 'bg-amber-500' : 'bg-red-500'
          }`}
          style={{ width: `${Math.min(Math.abs(pct) * 3, 100)}%` }}
        />
      </div>
      
      <p className="mt-3 text-xs text-gray-500 italic leading-relaxed">{note}</p>
    </div>
  );
};

export default TaxReconciliation;
